import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { get, post, put } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";

type AsyncState<T = any> = {
  data: T | null;
  loading: boolean;
  error: string | null;
};

type MutationState = {
  loading: boolean;
  error: string | null;
};

export type Poll1DraftPayload = {
  answers: (boolean | null)[];
  notes: string;
  evidence: Record<string, string>;
};

export type Poll2DraftPayload = {
  involvement: number | null;
  relationship: number | null;
  knowledge: number | null;
  comments: string;
  evidence: Record<string, string>;
};

export type SaveDraftLeaderboardResult = {
  period: string;
  included_cycle_ids: string[];
  added_cycle_ids: string[];
  updated_pending_approval: boolean;
  row_count: number;
};

const emptyAsync = <T = any>(): AsyncState<T> => ({
  data: null,
  loading: false,
  error: null,
});

const emptyMutation = (): MutationState => ({
  loading: false,
  error: null,
});

const toErrorMessage = (error: any, fallback: string): string => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (error?.message && typeof error.message === "string") return error.message;
  return fallback;
};

const unwrap = (response: any) => response?.data ?? response;

interface SponsorEngagementState {
  portfolio: AsyncState;
  myPerformance: AsyncState;
  teamFeedback: AsyncState;
  discoveryHub: AsyncState;
  launchContext: AsyncState;
  cycles: AsyncState;
  leaderboard: AsyncState;
  approvalPendingLeaderboard: AsyncState;
  executiveSummary: AsyncState;
  config: AsyncState;
  publishedLeaderboard: AsyncState;
  sponsorDrilldown: AsyncState;
  cycleResponses: AsyncState;
  cycleParticipantResponses: AsyncState;
  cycleLeaderboard: AsyncState;
  poll1Draft: AsyncState;
  poll2Draft: AsyncState;
  submitPoll1: MutationState;
  submitPoll2: MutationState;
  savePoll1Draft: MutationState;
  savePoll2Draft: MutationState;
  launchPoll: MutationState;
  extendPollDueDate: MutationState;
  saveDraftLeaderboard: MutationState;
  submitApproval: MutationState;
  changeApprover: MutationState;
  approveLeaderboard: MutationState;
  sendBackLeaderboard: MutationState;
  sponsorReminder: MutationState;
  poll2Reminder: MutationState;
  bulkReminders: MutationState;
  updateConfig: MutationState;
}

const initialState: SponsorEngagementState = {
  portfolio: emptyAsync(),
  myPerformance: emptyAsync(),
  teamFeedback: emptyAsync(),
  discoveryHub: emptyAsync(),
  launchContext: emptyAsync(),
  cycles: emptyAsync(),
  leaderboard: emptyAsync(),
  approvalPendingLeaderboard: emptyAsync(),
  executiveSummary: emptyAsync(),
  config: emptyAsync(),
  publishedLeaderboard: emptyAsync(),
  sponsorDrilldown: emptyAsync(),
  cycleResponses: emptyAsync(),
  cycleParticipantResponses: emptyAsync(),
  cycleLeaderboard: emptyAsync(),
  poll1Draft: emptyAsync(),
  poll2Draft: emptyAsync(),
  submitPoll1: emptyMutation(),
  submitPoll2: emptyMutation(),
  savePoll1Draft: emptyMutation(),
  savePoll2Draft: emptyMutation(),
  launchPoll: emptyMutation(),
  extendPollDueDate: emptyMutation(),
  saveDraftLeaderboard: emptyMutation(),
  submitApproval: emptyMutation(),
  changeApprover: emptyMutation(),
  approveLeaderboard: emptyMutation(),
  sendBackLeaderboard: emptyMutation(),
  sponsorReminder: emptyMutation(),
  poll2Reminder: emptyMutation(),
  bulkReminders: emptyMutation(),
  updateConfig: emptyMutation(),
};

export const fetchSponsorEngagementPortfolio = createAsyncThunk(
  "sponsorEngagement/fetchPortfolio",
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.sponsorEngagementPortfolio());
      return unwrap(response);
    } catch (error: any) {
      return rejectWithValue(toErrorMessage(error, "Failed to load check-ins"));
    }
  }
);

export const fetchSponsorEngagementMyPerformance = createAsyncThunk(
  "sponsorEngagement/fetchMyPerformance",
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.sponsorEngagementMyPerformance());
      return unwrap(response);
    } catch (error: any) {
      return rejectWithValue(
        toErrorMessage(error, "Failed to load performance")
      );
    }
  }
);

export const fetchTeamFeedback = createAsyncThunk(
  "sponsorEngagement/fetchTeamFeedback",
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.sponsorEngagementTeamFeedback());
      return unwrap(response);
    } catch (error: any) {
      return rejectWithValue(toErrorMessage(error, "Failed to load check-ins"));
    }
  }
);

export const fetchPoll1Draft = createAsyncThunk(
  "sponsorEngagement/fetchPoll1Draft",
  async (
    { accountId, cycleId }: { accountId: string; cycleId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await get(
        API_URLS.sponsorEngagementPoll1Draft(accountId, { cycle_id: cycleId })
      );
      const payload = unwrap(response);
      return payload?.draft ?? null;
    } catch {
      return null;
    }
  }
);

export const savePoll1Draft = createAsyncThunk(
  "sponsorEngagement/savePoll1Draft",
  async (
    {
      accountId,
      cycleId,
      draft,
    }: { accountId: string; cycleId: string; draft: Poll1DraftPayload },
    { rejectWithValue }
  ) => {
    try {
      await put(
        API_URLS.sponsorEngagementPoll1Draft(accountId, { cycle_id: cycleId }),
        {
          answers: draft.answers,
          notes: draft.notes ?? "",
          evidence: draft.evidence ?? {},
        }
      );
      return true;
    } catch (error: any) {
      return rejectWithValue(toErrorMessage(error, "Failed to save draft"));
    }
  }
);

export const submitPoll1 = createAsyncThunk(
  "sponsorEngagement/submitPoll1",
  async (
    {
      accountId,
      cycleId,
      payload,
    }: {
      accountId: string;
      cycleId: string;
      payload: {
        answers: boolean[];
        evidence: Record<string, string>;
        notes: string;
      };
    },
    { rejectWithValue }
  ) => {
    try {
      await post(
        API_URLS.sponsorEngagementPoll1(accountId, { cycle_id: cycleId }),
        payload
      );
      return true;
    } catch (error: any) {
      return rejectWithValue(toErrorMessage(error, "Submit failed"));
    }
  }
);

export const fetchPoll2Draft = createAsyncThunk(
  "sponsorEngagement/fetchPoll2Draft",
  async (
    {
      accountId,
      cycleId,
      sponsorId,
    }: { accountId: string; cycleId: string; sponsorId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await get(
        API_URLS.sponsorEngagementPoll2Draft(accountId, {
          cycle_id: cycleId,
          sponsor_id: sponsorId,
        })
      );
      const payload = unwrap(response);
      return payload?.draft ?? null;
    } catch {
      return null;
    }
  }
);

export const savePoll2Draft = createAsyncThunk(
  "sponsorEngagement/savePoll2Draft",
  async (
    {
      accountId,
      cycleId,
      sponsorId,
      draft,
    }: {
      accountId: string;
      cycleId: string;
      sponsorId: string;
      draft: Poll2DraftPayload;
    },
    { rejectWithValue }
  ) => {
    try {
      await put(
        API_URLS.sponsorEngagementPoll2Draft(accountId, {
          cycle_id: cycleId,
          sponsor_id: sponsorId,
        }),
        draft
      );
      return true;
    } catch (error: any) {
      return rejectWithValue(toErrorMessage(error, "Failed to save draft"));
    }
  }
);

export const submitPoll2 = createAsyncThunk(
  "sponsorEngagement/submitPoll2",
  async (
    {
      accountId,
      cycleId,
      sponsorId,
      payload,
    }: {
      accountId: string;
      cycleId: string;
      sponsorId: string;
      payload: Record<string, unknown>;
    },
    { rejectWithValue }
  ) => {
    try {
      await post(
        API_URLS.sponsorEngagementPoll2(accountId, {
          cycle_id: cycleId,
          sponsor_id: sponsorId,
        }),
        payload
      );
      return true;
    } catch (error: any) {
      return rejectWithValue(toErrorMessage(error, "Submit failed"));
    }
  }
);

export const fetchDiscoveryHub = createAsyncThunk(
  "sponsorEngagement/fetchDiscoveryHub",
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.sponsorEngagementDiscoveryHub());
      return unwrap(response);
    } catch (error: any) {
      return rejectWithValue(
        toErrorMessage(error, "Failed to load Discovery Hub")
      );
    }
  }
);

export const fetchLaunchContext = createAsyncThunk(
  "sponsorEngagement/fetchLaunchContext",
  async (period: string | undefined, { rejectWithValue }) => {
    try {
      const response = await get(
        API_URLS.sponsorEngagementLaunchContext(period ? { period } : undefined)
      );
      return unwrap(response);
    } catch (error: any) {
      return rejectWithValue(
        toErrorMessage(error, "Failed to load launch options")
      );
    }
  }
);

export const fetchCycles = createAsyncThunk(
  "sponsorEngagement/fetchCycles",
  async (_: void) => {
    try {
      const response = await get(API_URLS.sponsorEngagementCycles());
      const data = unwrap(response);
      return Array.isArray(data) ? data : [];
    } catch {
      return [];
    }
  }
);

export const fetchLeaderboard = createAsyncThunk(
  "sponsorEngagement/fetchLeaderboard",
  async (params: Record<string, any> | undefined, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.sponsorEngagementLeaderboard(params));
      return unwrap(response);
    } catch (error: any) {
      return rejectWithValue(
        toErrorMessage(error, "Failed to load leaderboard")
      );
    }
  }
);

export const fetchApprovalPendingLeaderboard = createAsyncThunk(
  "sponsorEngagement/fetchApprovalPendingLeaderboard",
  async (period: string, { rejectWithValue }) => {
    try {
      const response = await get(
        API_URLS.sponsorEngagementLeaderboard({
          period,
          view: "approval-pending",
        })
      );
      return unwrap(response);
    } catch (error: any) {
      return rejectWithValue(
        toErrorMessage(error, "Failed to load approval queue")
      );
    }
  }
);

export const fetchExecutiveSummary = createAsyncThunk(
  "sponsorEngagement/fetchExecutiveSummary",
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.sponsorEngagementExecutiveSummary());
      return unwrap(response);
    } catch (error: any) {
      return rejectWithValue(
        toErrorMessage(error, "Failed to load executive summary")
      );
    }
  }
);

export const fetchEngagementConfig = createAsyncThunk(
  "sponsorEngagement/fetchConfig",
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.sponsorEngagementConfig());
      return unwrap(response);
    } catch (error: any) {
      return rejectWithValue(
        toErrorMessage(error, "Failed to load program settings")
      );
    }
  }
);

export const updateEngagementConfig = createAsyncThunk(
  "sponsorEngagement/updateConfig",
  async (payload: Record<string, unknown>, { rejectWithValue }) => {
    try {
      const response = await put(
        API_URLS.sponsorEngagementPutConfig(),
        payload
      );
      return unwrap(response);
    } catch (error: any) {
      return rejectWithValue(toErrorMessage(error, "Save failed"));
    }
  }
);

export const fetchPublishedLeaderboard = createAsyncThunk(
  "sponsorEngagement/fetchPublishedLeaderboard",
  async (period?: string) => {
    try {
      const params: Record<string, unknown> = { published: true };
      if (period) params.period = period;
      const response = await get(API_URLS.sponsorEngagementLeaderboard(params));
      return unwrap(response);
    } catch {
      return { period: undefined, publication_state: null, rows: [] };
    }
  }
);

export const fetchSponsorDrilldown = createAsyncThunk(
  "sponsorEngagement/fetchSponsorDrilldown",
  async (
    {
      sponsorId,
      params,
    }: { sponsorId: string; params?: Record<string, string> },
    { rejectWithValue }
  ) => {
    try {
      const response = await get(
        API_URLS.sponsorEngagementDrilldown(sponsorId, params)
      );
      return unwrap(response);
    } catch (error: any) {
      return rejectWithValue(
        toErrorMessage(error, "Failed to load sponsor drilldown")
      );
    }
  }
);

export const fetchCycleResponses = createAsyncThunk(
  "sponsorEngagement/fetchCycleResponses",
  async (cycleId: string, { rejectWithValue }) => {
    try {
      const response = await get(
        API_URLS.sponsorEngagementCycleResponses(cycleId)
      );
      return unwrap(response);
    } catch (error: any) {
      return rejectWithValue(
        toErrorMessage(error, "Failed to load cycle responses")
      );
    }
  }
);

export const fetchCycleParticipantResponses = createAsyncThunk(
  "sponsorEngagement/fetchCycleParticipantResponses",
  async (
    {
      cycleId,
      sponsorId,
      teamMemberId,
    }: {
      cycleId: string;
      sponsorId?: string;
      teamMemberId?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await get(
        API_URLS.sponsorEngagementCycleResponses(cycleId, {
          sponsorId,
          teamMemberId,
        })
      );
      return unwrap(response);
    } catch (error: any) {
      return rejectWithValue(
        toErrorMessage(error, "Failed to load participant responses")
      );
    }
  }
);

export const fetchCycleLeaderboard = createAsyncThunk(
  "sponsorEngagement/fetchCycleLeaderboard",
  async (cycleId: string, { rejectWithValue }) => {
    try {
      const response = await get(
        API_URLS.sponsorEngagementCycleLeaderboard(cycleId)
      );
      return unwrap(response);
    } catch (error: any) {
      return rejectWithValue(
        toErrorMessage(error, "Failed to load cycle leaderboard")
      );
    }
  }
);

export const launchPoll = createAsyncThunk(
  "sponsorEngagement/launchPoll",
  async (payload: Record<string, unknown>, { rejectWithValue }) => {
    try {
      const response = await post(API_URLS.sponsorEngagementLaunch(), payload);
      return unwrap(response);
    } catch (error: any) {
      return rejectWithValue(toErrorMessage(error, "Launch failed"));
    }
  }
);

export const extendPollDueDate = createAsyncThunk(
  "sponsorEngagement/extendPollDueDate",
  async (
    payload: { cycleId: string; due_date: string; reason: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await put(
        API_URLS.sponsorEngagementExtendDueDate(payload.cycleId),
        { due_date: payload.due_date, reason: payload.reason }
      );
      return unwrap(response);
    } catch (error: any) {
      return rejectWithValue(
        toErrorMessage(error, "Failed to extend due date")
      );
    }
  }
);

export const saveDraftLeaderboard = createAsyncThunk(
  "sponsorEngagement/saveDraftLeaderboard",
  async (
    { period, cycleIds }: { period: string; cycleIds: string[] },
    { rejectWithValue }
  ) => {
    try {
      const response = await post(
        API_URLS.sponsorEngagementSaveDraftLeaderboard({ period }),
        { cycle_ids: cycleIds }
      );
      return unwrap(response) as SaveDraftLeaderboardResult;
    } catch (error: any) {
      return rejectWithValue(
        toErrorMessage(error, "Failed to save draft leaderboard")
      );
    }
  }
);

export const submitApproval = createAsyncThunk(
  "sponsorEngagement/submitApproval",
  async (
    payload: { review_notes: string; approver_id: string },
    { rejectWithValue }
  ) => {
    try {
      await post(API_URLS.sponsorEngagementSubmitApproval(), payload);
      return true;
    } catch (error: any) {
      return rejectWithValue(
        toErrorMessage(error, "Failed to submit for approval")
      );
    }
  }
);

export const changeApprover = createAsyncThunk(
  "sponsorEngagement/changeApprover",
  async (payload: Record<string, unknown>, { rejectWithValue }) => {
    try {
      await post(API_URLS.sponsorEngagementChangeApprover(), payload);
      return true;
    } catch (error: any) {
      return rejectWithValue(
        toErrorMessage(error, "Failed to change approver")
      );
    }
  }
);

export const approveLeaderboard = createAsyncThunk(
  "sponsorEngagement/approveLeaderboard",
  async (_: void, { rejectWithValue }) => {
    try {
      await post(API_URLS.sponsorEngagementApprove(), {});
      return true;
    } catch (error: any) {
      return rejectWithValue(toErrorMessage(error, "Approval failed"));
    }
  }
);

export const sendBackLeaderboard = createAsyncThunk(
  "sponsorEngagement/sendBackLeaderboard",
  async (payload: Record<string, unknown>, { rejectWithValue }) => {
    try {
      await post(API_URLS.sponsorEngagementSendBack(), payload);
      return true;
    } catch (error: any) {
      return rejectWithValue(toErrorMessage(error, "Send back failed"));
    }
  }
);

export const sendSponsorReminder = createAsyncThunk(
  "sponsorEngagement/sendSponsorReminder",
  async (
    { sponsorId, cycleId }: { sponsorId: string; cycleId?: string },
    { rejectWithValue }
  ) => {
    try {
      const params = cycleId ? { cycle_id: cycleId } : undefined;
      await post(API_URLS.sponsorEngagementReminder(sponsorId, params), {});
      return true;
    } catch (error: any) {
      return rejectWithValue(toErrorMessage(error, "Reminder failed"));
    }
  }
);

export const sendPoll2Reminder = createAsyncThunk(
  "sponsorEngagement/sendPoll2Reminder",
  async (
    {
      teamMemberId,
      sponsorId,
      cycleId,
    }: { teamMemberId: string; sponsorId: string; cycleId: string },
    { rejectWithValue }
  ) => {
    try {
      await post(
        API_URLS.sponsorEngagementPoll2Reminder(teamMemberId, {
          cycle_id: cycleId,
          sponsor_id: sponsorId,
        }),
        {}
      );
      return true;
    } catch (error: any) {
      return rejectWithValue(toErrorMessage(error, "Reminder failed"));
    }
  }
);

export const sendBulkReminders = createAsyncThunk(
  "sponsorEngagement/sendBulkReminders",
  async (
    payload: {
      poll1_targets: { sponsor_id: string; cycle_id?: string }[];
      poll2_targets: {
        team_member_id: string;
        sponsor_id: string;
        cycle_id: string;
      }[];
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await post(
        API_URLS.sponsorEngagementBulkReminders(),
        payload
      );
      return unwrap(response);
    } catch (error: any) {
      return rejectWithValue(toErrorMessage(error, "Bulk reminder failed"));
    }
  }
);

const bindAsync = (
  builder: any,
  thunk: any,
  key: keyof SponsorEngagementState
) => {
  builder
    .addCase(thunk.pending, (state: SponsorEngagementState) => {
      (state[key] as AsyncState).loading = true;
      (state[key] as AsyncState).error = null;
    })
    .addCase(thunk.fulfilled, (state: SponsorEngagementState, action: any) => {
      (state[key] as AsyncState).loading = false;
      (state[key] as AsyncState).data = action.payload;
      (state[key] as AsyncState).error = null;
    })
    .addCase(thunk.rejected, (state: SponsorEngagementState, action: any) => {
      (state[key] as AsyncState).loading = false;
      (state[key] as AsyncState).error = (action.payload as string) || null;
    });
};

const bindMutation = (
  builder: any,
  thunk: any,
  key: keyof SponsorEngagementState
) => {
  builder
    .addCase(thunk.pending, (state: SponsorEngagementState) => {
      (state[key] as MutationState).loading = true;
      (state[key] as MutationState).error = null;
    })
    .addCase(thunk.fulfilled, (state: SponsorEngagementState) => {
      (state[key] as MutationState).loading = false;
      (state[key] as MutationState).error = null;
    })
    .addCase(thunk.rejected, (state: SponsorEngagementState, action: any) => {
      (state[key] as MutationState).loading = false;
      (state[key] as MutationState).error = (action.payload as string) || null;
    });
};

const SponsorEngagementSlice = createSlice({
  name: "sponsorEngagement",
  initialState,
  reducers: {},
  extraReducers: builder => {
    bindAsync(builder, fetchSponsorEngagementPortfolio, "portfolio");
    bindAsync(builder, fetchSponsorEngagementMyPerformance, "myPerformance");
    bindAsync(builder, fetchTeamFeedback, "teamFeedback");
    bindAsync(builder, fetchDiscoveryHub, "discoveryHub");
    bindAsync(builder, fetchLaunchContext, "launchContext");
    bindAsync(builder, fetchCycles, "cycles");
    bindAsync(builder, fetchLeaderboard, "leaderboard");
    bindAsync(
      builder,
      fetchApprovalPendingLeaderboard,
      "approvalPendingLeaderboard"
    );
    bindAsync(builder, fetchExecutiveSummary, "executiveSummary");
    bindAsync(builder, fetchEngagementConfig, "config");
    bindAsync(builder, fetchPublishedLeaderboard, "publishedLeaderboard");
    bindAsync(builder, fetchSponsorDrilldown, "sponsorDrilldown");
    bindAsync(builder, fetchCycleResponses, "cycleResponses");
    bindAsync(
      builder,
      fetchCycleParticipantResponses,
      "cycleParticipantResponses"
    );
    bindAsync(builder, fetchCycleLeaderboard, "cycleLeaderboard");
    bindAsync(builder, fetchPoll1Draft, "poll1Draft");
    bindAsync(builder, fetchPoll2Draft, "poll2Draft");

    bindMutation(builder, submitPoll1, "submitPoll1");
    bindMutation(builder, submitPoll2, "submitPoll2");
    bindMutation(builder, savePoll1Draft, "savePoll1Draft");
    bindMutation(builder, savePoll2Draft, "savePoll2Draft");
    bindMutation(builder, launchPoll, "launchPoll");
    bindMutation(builder, extendPollDueDate, "extendPollDueDate");
    bindMutation(builder, saveDraftLeaderboard, "saveDraftLeaderboard");
    bindMutation(builder, submitApproval, "submitApproval");
    bindMutation(builder, changeApprover, "changeApprover");
    bindMutation(builder, approveLeaderboard, "approveLeaderboard");
    bindMutation(builder, sendBackLeaderboard, "sendBackLeaderboard");
    bindMutation(builder, sendSponsorReminder, "sponsorReminder");
    bindMutation(builder, sendPoll2Reminder, "poll2Reminder");
    bindMutation(builder, sendBulkReminders, "bulkReminders");
    bindMutation(builder, updateEngagementConfig, "updateConfig");
  },
});

export default SponsorEngagementSlice.reducer;
